(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  class ScrollTo {
    /**
     * Initializes a new instance of the class.
     * Sets the initial state to null.
     */
    constructor() {
      this.state = null;
      this.eventsToObserve = {
        scrollByDistance: this.distance.bind(this),
        scrollByIndex: this.index.bind(this),
      };
    }

    /**
     * Initializes the carousel scroll components with the provided state.
     *
     * @param {Object} state - The state object used to initialize the scroll body and scroll target.
     */
    initialize(state) {
      this.state = state;
      this.scrollBody = new global.NewHomeSource.Carousel.ScrollBody();
      this.scrollBody.initialize(this.state);
      this.scrollTarget = new global.NewHomeSource.Carousel.ScrollTarget();
      this.scrollTarget.initialize(this.state);
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
      return this;
    }

    /**
     * Scrolls the carousel to the specified target position.
     *
     * Updates the target vector, manages animation state, and updates the current and previous indices.
     * Notifies observers to start, update, or render the animation based on the distance and duration.
     *
     * @param {Object} target - The target scroll position and index.
     * @param {number} target.distance - The distance to scroll.
     * @param {number} target.index - The target index to scroll to.
     */
    scrollTo(target) {
      const index = this.state.get('index');
      const indexPrevious = this.state.get('indexPrevious');
      const targetVector = this.state.get('target');
      const duration = this.state.get('duration');
      const distanceDiff = target.distance;
      const indexDiff = target.index !== index.get();

      targetVector.add(distanceDiff);

      if (distanceDiff) {
        if (duration) {
          this.state.notifyObservers('startAnimation');
        } else {
          this.state.notifyObservers('updateAnimation');
          this.state.notifyObservers('renderAnimation', { alpha: 1 });
          this.state.notifyObservers('updateAnimation');
        }
      }

      if (indexDiff) {
        indexPrevious.set(index.get());
        index.set(target.index);
        this.state.notifyObservers('carouselChange');
      }
    }

    /**
     * Scrolls to a target position based on the provided distance data.
     *
     * @param {Object} data - The data containing scroll parameters.
     * @param {number} data.number - The distance to scroll.
     * @param {boolean} data.snap - Whether to snap to the target position.
     */
    distance(data) {
      const number = data.number;
      const snap = data.snap;
      const target = this.scrollTarget.byDistance(number, snap);
      this.scrollTo(target);
    }

    /**
     * Scrolls the carousel to a specific item based on the provided data.
     *
     * @param {Object} data - The data containing scroll information.
     * @param {number} data.number - The index number to scroll to.
     * @param {number} data.direction - The direction in which to scroll (e.g., 1 for forward, -1 for backward).
     */
    index(data) {
      const number = data.number;
      const direction = data.direction;
      const index = this.state.get('index');
      const targetIndex = index.clone().set(number);
      const target = this.scrollTarget.byIndex(targetIndex.get(), direction);
      this.scrollTo(target);
    }

    /**
     * Updates the observer for a given topic by invoking its callback with the provided data.
     *
     * @param {*} data - The data to pass to the callback function.
     * @param {string} topic - The topic whose observer callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }

    /**
     * Removes all event observers associated with this instance from the state.
     * Iterates over each event in `eventsToObserve` and unregisters this instance as an observer.
     */
    destroy() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }
  }

  global.NewHomeSource.Carousel.ScrollTo = ScrollTo;
}(window));
